import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, XCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/booking";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function TableSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const token = params.get("token");
  const [state, setState] = useState("polling");
  const [booking, setBooking] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // On-arrival / voucher path — no Stripe, fetch by manage token
    if (token && !sessionId) {
      (async () => {
        try {
          const res = await api.get(`/table-bookings/manage/${token}`);
          setBooking(res.data);
          setState("success");
        } catch (e) {
          setState("failed");
        }
      })();
      return;
    }

    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid" && res.data.booking) {
          setBooking(res.data.booking);
          setState("success");
          return;
        }
        if (res.data.status === "expired") {
          setState("failed");
          return;
        }
      } catch (e) {}
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
  }, [sessionId, token]);

  const onArrival = booking?.payment_method === "on_arrival";

  return (
    <div className="min-h-screen bg-[#1C1917] py-16 px-6" data-testid="table-success-page">
      <div className="max-w-2xl mx-auto">
        {state === "polling" && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-12 text-center" data-testid="table-processing">
            <Loader2 className="h-10 w-10 animate-spin text-[#722F37] mx-auto" />
            <h1 className="mt-6 font-serif text-2xl text-[#1C1917]">Confirming your booking…</h1>
            <p className="mt-2 text-[#57534E]">Please wait a moment.</p>
          </div>
        )}

        {state === "success" && booking && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-8 md:p-12 text-center animate-fade-up" data-testid="table-confirmation">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#722F37]/10 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-9 w-9 text-[#722F37]" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#1C1917]">
              You're booked in, {booking.name.split(" ")[0]}!
            </h2>
            <p className="mt-4 text-[#57534E] text-lg">
              {onArrival ? "Booking confirmed. " : "Deposit received. "}A confirmation has been sent to{" "}
              <span className="font-semibold text-[#1C1917]">{booking.email}</span>. See you Friday night at the wine bar.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
              <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
                <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold">Date</p>
                <p className="mt-1 font-medium text-[#1C1917]">{fmtDate(booking.date)}</p>
              </div>
              <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
                <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold">Arrival</p>
                <p className="mt-1 font-medium text-[#1C1917]">{booking.arrival_time}</p>
              </div>
              <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
                <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold">Party</p>
                <p className="mt-1 font-medium text-[#1C1917]">{booking.party_size} people</p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-[#722F37]/20 bg-[#722F37]/5 p-4 text-left text-sm">
              {booking.cheeseboards > 0 && (
                <div className="flex justify-between text-[#57534E] mb-1.5">
                  <span>Cheeseboards · $42.00 × {booking.cheeseboards}</span>
                  <span className="font-medium text-[#1C1917]">${(booking.cheeseboards * 42).toFixed(2)}</span>
                </div>
              )}
              {onArrival ? (
                <>
                  <div className="flex justify-between font-semibold text-[#1C1917]">
                    <span>{booking.cheeseboards > 0 ? "Due on arrival" : "Payment"}</span>
                    <span data-testid="table-payment-note">
                      {booking.cheeseboards > 0 ? `$${(booking.cheeseboards * 42).toFixed(2)} AUD` : "Pay at the bar"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-[#57534E]">
                    No payment was taken now — {booking.cheeseboards > 0 ? "cheeseboards and drinks are" : "drinks are"} settled on arrival at the cellar door.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex justify-between font-semibold text-[#1C1917]">
                    <span>Paid</span>
                    <span data-testid="table-deposit-paid">${(booking.deposit_paid ?? 50).toFixed(2)} AUD</span>
                  </div>
                  <p className="mt-1 text-xs text-[#57534E]">
                    The $50 deposit is deducted from your final bill. The balance is payable on the night.
                  </p>
                </>
              )}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={`/table/manage/${booking.manage_token}`}
                data-testid="table-manage-link"
                className="px-8 py-3 rounded-full bg-[#722F37] text-white font-semibold hover:opacity-90 transition-all"
              >
                Manage or cancel
              </a>
              <a
                href="/"
                className="px-8 py-3 rounded-full border border-[#722F37] text-[#722F37] font-semibold hover:bg-[#722F37] hover:text-white transition-all"
              >
                Back to site
              </a>
            </div>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-12 text-center" data-testid="table-payment-failed">
            <XCircle className="h-12 w-12 text-[#722F37] mx-auto" />
            <h1 className="mt-4 font-serif text-2xl text-[#1C1917]">
              {state === "timeout" ? "Still processing your booking" : "Booking not completed"}
            </h1>
            <p className="mt-2 text-[#57534E]">
              {state === "timeout"
                ? "This is taking longer than expected. If you were charged, you'll receive a confirmation email shortly."
                : "We couldn't confirm your booking. Please try again."}
            </p>
            <Link to="/#dine" className="mt-6 inline-block px-6 py-3 rounded-full bg-[#722F37] text-white font-semibold hover:opacity-90 transition-all">
              Back to wine bar booking
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
